export const INVENTORY_QUANTITY_DRAFT_VERSION = 1;
export const INVENTORY_QUANTITY_DRAFT_PREFIX =
  "vendororbis:inventory-quantity-draft";

export type QuantityDraftItem = {
  originalQuantity: number;
  draftQuantity: number;
};

export type InventoryQuantityDraft = {
  version: 1;
  businessId: string;
  userId: string;
  poolId: string;
  editMode: boolean;
  updatedAt: string;
  items: Record<string, QuantityDraftItem>;
};

export const inventoryQuantityDraftKey = (
  businessId: string,
  userId: string,
  poolId: string
) =>
  `${INVENTORY_QUANTITY_DRAFT_PREFIX}:v${INVENTORY_QUANTITY_DRAFT_VERSION}:${encodeURIComponent(
    businessId
  )}:${encodeURIComponent(userId)}:${encodeURIComponent(poolId)}`;

const isSafeQuantity = (value: unknown) =>
  Number.isSafeInteger(value) && Number(value) >= 0;

export const readInventoryQuantityDraft = (
  storage: Pick<Storage, "getItem">,
  businessId: string,
  userId: string,
  poolId: string
): InventoryQuantityDraft | null => {
  try {
    const raw = storage.getItem(
      inventoryQuantityDraftKey(businessId, userId, poolId)
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed?.version !== INVENTORY_QUANTITY_DRAFT_VERSION ||
      parsed?.businessId !== businessId ||
      parsed?.userId !== userId ||
      parsed?.poolId !== poolId ||
      typeof parsed?.editMode !== "boolean" ||
      typeof parsed?.updatedAt !== "string" ||
      !parsed?.items ||
      typeof parsed.items !== "object" ||
      Array.isArray(parsed.items)
    ) {
      return null;
    }
    const items: Record<string, QuantityDraftItem> = {};
    for (const [itemId, value] of Object.entries(parsed.items)) {
      const item = value as Partial<QuantityDraftItem>;
      if (
        !itemId ||
        !isSafeQuantity(item.originalQuantity) ||
        !isSafeQuantity(item.draftQuantity)
      ) {
        return null;
      }
      items[itemId] = {
        originalQuantity: Number(item.originalQuantity),
        draftQuantity: Number(item.draftQuantity),
      };
    }
    return { ...parsed, items } as InventoryQuantityDraft;
  } catch {
    return null;
  }
};

export const writeInventoryQuantityDraft = (
  storage: Pick<Storage, "setItem">,
  draft: InventoryQuantityDraft
) => {
  storage.setItem(
    inventoryQuantityDraftKey(draft.businessId, draft.userId, draft.poolId),
    JSON.stringify(draft)
  );
};

export const clearInventoryQuantityDraft = (
  storage: Pick<Storage, "removeItem">,
  businessId: string,
  userId: string,
  poolId: string
) => {
  storage.removeItem(inventoryQuantityDraftKey(businessId, userId, poolId));
};
